import { useEffect, useState } from "react";
import { api } from "../../lib/api";

export default function RevenueDashboard() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [cards, setCards] = useState([]);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    const [s, r, c, rec] = await Promise.all([
      api.get(`/tracking/summary?days=${days}`),
      api.get(`/tracking/by-route?days=${days}&limit=25`),
      api.get(`/tracking/by-card?days=${Math.max(days, 90)}&limit=50`),
      api.get(`/tracking/recent?limit=30`),
    ]);
    setSummary(s.data);
    setRoutes(r.data.items);
    setCards(c.data.items);
    setRecent(rec.data.items);
  };
  useEffect(() => { load(); }, [days]);

  if (!summary) return <div className="p-10 font-mono text-sm">Loading…</div>;

  const booking = summary.by_type?.booking_click || { clicks: 0, commission: 0 };
  const card = summary.by_type?.card_click || { clicks: 0, commission: 0 };

  return (
    <div className="p-8 md:p-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-[#B4533C]">Revenue</div>
          <h1 className="font-serif text-4xl mt-1">Affiliate performance</h1>
          <p className="mt-2 text-sm text-[#4A4742]">Unified view of booking clicks (Travelpayouts) and credit-card apply clicks. Commission is estimated from configurable per-click rates.</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`revenue-range-${d}`}
              className={`border px-4 py-2 text-xs font-mono uppercase tracking-wider ${days === d ? "bg-[#1B3B36] text-white border-[#1B3B36]" : "border-[#D6CFC4]"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 grid-borders border-t border-l border-[#D6CFC4]" data-testid="revenue-kpis">
        <Kpi label="Total clicks" v={summary.total_clicks} />
        <Kpi label="Est. commission" v={`$${summary.total_commission_estimate.toFixed(2)}`} accent />
        <Kpi label="Booking clicks" v={`${booking.clicks} · $${booking.commission.toFixed(0)}`} />
        <Kpi label="Card apply clicks" v={`${card.clicks} · $${card.commission.toFixed(0)}`} />
      </div>

      <section className="mt-10">
        <h2 className="font-serif text-2xl mb-3">Clicks per day</h2>
        <Sparkline series={summary.series} />
      </section>

      <div className="mt-12 grid lg:grid-cols-2 gap-10">
        <section>
          <h2 className="font-serif text-2xl mb-3">Top booking routes</h2>
          <div className="border border-[#D6CFC4]">
            <table className="w-full admin-table" data-testid="revenue-routes-table">
              <thead className="bg-[#EFECE5]"><tr>
                <th className="p-3 text-left">Destination</th><th className="p-3 text-left">Origins</th><th className="p-3 text-right">Clicks</th><th className="p-3 text-right">Est. $</th>
              </tr></thead>
              <tbody>
                {routes.map((r) => (
                  <tr key={r.destination} className="border-t border-[#D6CFC4]">
                    <td className="p-3 font-mono text-xs">{r.destination}</td>
                    <td className="p-3 text-xs">{r.origin_count}</td>
                    <td className="p-3 text-right font-mono text-xs">{r.clicks}</td>
                    <td className="p-3 text-right font-mono text-xs">${r.commission.toFixed(2)}</td>
                  </tr>
                ))}
                {routes.length === 0 && <Empty cols={4} msg="No booking clicks tracked yet. Browse /booking to generate traffic." />}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Top performing cards</h2>
          <div className="border border-[#D6CFC4]">
            <table className="w-full admin-table" data-testid="revenue-cards-table">
              <thead className="bg-[#EFECE5]"><tr>
                <th className="p-3 text-left">Card</th><th className="p-3 text-left">Issuer</th><th className="p-3 text-right">Articles</th><th className="p-3 text-right">Clicks</th><th className="p-3 text-right">Est. $</th>
              </tr></thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={c.card_id} className="border-t border-[#D6CFC4]">
                    <td className="p-3 text-xs">{c.card_name}</td>
                    <td className="p-3 text-xs">{c.issuer}</td>
                    <td className="p-3 text-right font-mono text-xs">{c.article_count}</td>
                    <td className="p-3 text-right font-mono text-xs">{c.clicks}</td>
                    <td className="p-3 text-right font-mono text-xs">${c.commission.toFixed(2)}</td>
                  </tr>
                ))}
                {cards.length === 0 && <Empty cols={5} msg="No card apply clicks yet. Clicks are logged automatically from /credit-cards and any article affiliate block." />}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="mt-12">
        <h2 className="font-serif text-2xl mb-3">Recent clicks</h2>
        <div className="border border-[#D6CFC4]">
          <table className="w-full admin-table">
            <thead className="bg-[#EFECE5]"><tr>
              <th className="p-3 text-left">Time</th><th className="p-3 text-left">Type</th><th className="p-3 text-left">Detail</th><th className="p-3 text-left">Referrer</th>
            </tr></thead>
            <tbody>
              {recent.map((e) => (
                <tr key={e.id} className="border-t border-[#D6CFC4]">
                  <td className="p-3 font-mono text-xs">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs font-mono">{e.event_type}</td>
                  <td className="p-3 text-xs">{e.event_type === "booking_click" ? `${e.origin || "?"} → ${e.destination || "?"}${e.price_cash ? ` · $${e.price_cash}` : ""}` : `${e.card_name || e.card_id}`}</td>
                  <td className="p-3 text-xs font-mono text-[#4A4742]">{e.referrer_path || "—"}</td>
                </tr>
              ))}
              {recent.length === 0 && <Empty cols={4} msg="No recent events." />}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, v, accent }) {
  return (
    <div className="p-5">
      <div className="overline text-[#4A4742]">{label}</div>
      <div className={`font-serif text-3xl mt-1 ${accent ? "text-[#B4533C]" : ""}`}>{v}</div>
    </div>
  );
}
function Empty({ cols, msg }) {
  return <tr><td colSpan={cols} className="p-6 text-center text-xs text-[#4A4742]">{msg}</td></tr>;
}

function Sparkline({ series }) {
  if (!series?.length) return <div className="text-sm text-[#4A4742]">No activity yet.</div>;
  const max = Math.max(1, ...series.map((d) => (d.booking_click || 0) + (d.card_click || 0)));
  return (
    <div className="border border-[#D6CFC4] bg-white p-5">
      <div className="flex items-end gap-1.5 h-32">
        {series.map((d) => {
          const total = (d.booking_click || 0) + (d.card_click || 0);
          const bookingPct = total ? ((d.booking_click || 0) / total) * 100 : 0;
          return (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${total} clicks · $${(d.commission || 0).toFixed(2)}`}>
              <div className="w-full flex flex-col justify-end" style={{ height: `${(total / max) * 100}%`, minHeight: "2px" }}>
                <div className="bg-[#B4533C]" style={{ height: `${100 - bookingPct}%` }} />
                <div className="bg-[#1B3B36]" style={{ height: `${bookingPct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex gap-4 text-xs font-mono">
        <span className="flex items-center gap-2"><span className="w-3 h-3 bg-[#1B3B36]" /> Booking</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 bg-[#B4533C]" /> Cards</span>
      </div>
    </div>
  );
}
